import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_340: DocMigration = {
  maxSchemaVersion: "0.0.340",
  description: "Replaces plan visibility settings with entity tags.",
  apply(json: any) {
    json.entityTags = PlanVisibilityProps.reduce((table, tag, i) => {
      table[tag] = entityTagNew({ id: tag, name: PlanVisibilityNames[i] });
      return table;
    }, {});

    const floorPlanOverride = viewTemplateOverrideNew({
      name: "Floor Plan Overrides",
      tags: { addToFloorPlan: true },
      categories: [...FilterableAnnotationCategories, "CEILING"],
    });
    const electricalPlanOverride = viewTemplateOverrideNew({
      name: "Electrical Plan Overrides",
      tags: { addToElectricalPlan: true },
      categories: [...FilterableAnnotationCategories, "CEILING"],
    });
    const foundationPlanOverride = viewTemplateOverrideNew({
      name: "Foundation Plan Overrides",
      tags: { addToFoundationPlan: true },
      categories: [...FilterableAnnotationCategories, "CEILING", "PROP", "PROP_TAG"],
    });

    json.viewTemplateOverrides = {};

    const annotationTypesToEntityTag: { [key: string]: EntityTag } = {};

    function convertAnnotationTypeToEntityTag(type: string): EntityTag {
      if (annotationTypesToEntityTag[type] === undefined) {
        const newTag = entityTagNew({ name: type });
        annotationTypesToEntityTag[type] = newTag;
        json.entityTags[newTag.id] = newTag;
        return newTag;
      }

      return annotationTypesToEntityTag[type];
    }

    for (const id in json.annotation) {
      const annotation = json.annotation[id];
      convertPlanVisibilitySettingsToEntityTags(annotation);

      if (!annotation.annotationType) {
        continue;
      }

      const tagFromType = convertAnnotationTypeToEntityTag(annotation.annotationType);
      if (tagFromType) {
        annotation.tags[tagFromType.id] = true;
      }
      delete annotation.annotationType;
    }

    for (const id in json.viewTemplates) {
      const viewTemplate = json.viewTemplates[id];

      viewTemplate.categories = viewTemplate.layers;
      delete viewTemplate.layers;

      viewTemplate.viewTemplateOverrideIds = [];
      switch (viewTemplate.referenceType) {
        case "FLOOR":
          viewTemplate.viewTemplateOverrideIds = [floorPlanOverride.id];
          json.viewTemplateOverrides[floorPlanOverride.id] = floorPlanOverride;
          break;
        case "ELECTRICAL":
          viewTemplate.viewTemplateOverrideIds = [electricalPlanOverride.id];
          json.viewTemplateOverrides[electricalPlanOverride.id] = electricalPlanOverride;
          break;
        case "FOUNDATION":
          viewTemplate.viewTemplateOverrideIds = [foundationPlanOverride.id];
          json.viewTemplateOverrides[foundationPlanOverride.id] = foundationPlanOverride;
          break;
        default:
          break;
      }
    }

    for (const id in json.sheets) {
      const sheet = json.sheets[id];

      if (sheet.type === "DRAWINGS") {
        continue;
      }

      sheet.tags = {};
      for (const annotationType of sheet.annotationTypes as string[]) {
        const tagFromType = convertAnnotationTypeToEntityTag(annotationType);
        if (tagFromType) {
          sheet.tags[tagFromType.id] = true;
        }
      }
      delete sheet.annotationTypes;
    }

    for (const id in json.ceilings) {
      const ceiling = json.ceilings[id];
      convertPlanVisibilitySettingsToEntityTags(ceiling);
    }

    for (const id in json.props) {
      const prop = json.props[id];
      convertPlanVisibilitySettingsToEntityTags(prop);
    }

    for (const id in json.settings) {
      const settings = json.settings[id];
      if (settings.type === "ANNOTATION_CATALOG_SETTINGS") {
        for (const templateId in settings.annotationTemplates) {
          const template = settings.annotationTemplates[templateId];
          convertPlanVisibilitySettingsToEntityTags(template);
        }
      }
    }

    for (const id in json.planViews) {
      const planViewFilter = json.planViews[id].viewFilter;

      for (const prop in ViewFilterPropToPlanVisibilityProp) {
        const planVisibilitySettingProp = ViewFilterPropToPlanVisibilityProp[prop];
        if (planViewFilter[prop]) {
          planViewFilter.tags ??= {};
          planViewFilter.tags[planVisibilitySettingProp] = planViewFilter[prop];
          delete planViewFilter[prop];
        }
      }
    }

    return json;
  },
};

interface EntityTag {
  readonly entityType: "ENTITY_TAG";
  readonly id: string;
  readonly name: string;
  readonly sortIndex: number;
}

interface ViewTemplateOverride {
  readonly entityType: "VIEW_TEMPLATE_OVERRIDE";
  readonly id: string;
  readonly name: string;
  readonly sortIndex: number;
  readonly tags: { [key: string]: boolean };
  readonly categories: string[];
}

function convertPlanVisibilitySettingsToEntityTags(entity: any) {
  entity.tags ??= {};
  for (const tag of PlanVisibilityProps) {
    if (entity[tag] !== undefined) {
      entity.tags[tag] = entity[tag];
      delete entity[tag];
    }
  }
}

function entityTagNew({ id = uid(), name = "Entity Tag" }): EntityTag {
  return {
    entityType: "ENTITY_TAG",
    id,
    name,
    sortIndex: 0,
  };
}

function viewTemplateOverrideNew({
  name = "View Template Override",
  tags = {},
  categories = [],
}: Partial<ViewTemplateOverride>): ViewTemplateOverride {
  return {
    entityType: "VIEW_TEMPLATE_OVERRIDE",
    id: uid(),
    name,
    sortIndex: 0,
    tags,
    categories,
  };
}

const PlanVisibilityProps = [
  "addToElectricalPlan",
  "addToFloorPlan",
  "addToFoundationPlan",
  "addToPlumbingPlan",
  "addToMechanicalPlan",
  "addToStructuralPlan",
  "addToShowroomPlan",
  "addToBrochurePlan",
  "addToRoofPlan",
  "addToUtilityPlan",
] as const;

const ViewFilterPropToPlanVisibilityProp = PlanVisibilityProps.reduce((acc, prop) => {
  acc[prop.replace("addTo", "annotation")] = prop;
  return acc;
}, {});

const PlanVisibilityNames = [
  "Add To Electrical Plan",
  "Add To Floor Plan",
  "Add To Foundation Plan",
  "Add To Plumbing Plan",
  "Add To Mechanical Plan",
  "Add To Structural Plan",
  "Add To Showroom Plan",
  "Add To Brochure Plan",
  "Add To Roof Plan",
  "Add To Utility Plan",
] as const;

const FilterableAnnotationCategories: string[] = [
  "ANNOTATION",
  "ANNOT_LINEWORK_FILL",
  "ANNOT_LINEWORK_LINE",
  "ANNOT_LINEWORK_OFFSETLINE",
  "ANNOT_NOTE",
  "ANNOT_DIM_GROUP",
  "ANNOT_DIM_LABEL",
  "ANNOT_DIM_LINE",
  "ANNOT_DIM_LINEWORK",
  "ANNOT_DIM_TICK",
  "ANNOT_DIM_WITNESS",
];

export default migration_0_0_340;
